import { useRef, useCallback } from 'react';
import type { ViewportInfo } from '../hooks/useFabricCanvas';

interface CanvasAreaProps {
  canvasElRef: React.RefObject<HTMLCanvasElement | null>;
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  onFileDropped?: (file: File) => void;
  onFileSelected?: (file: File) => void;
  hasImage: boolean;
  gridVisible: boolean;
  viewport: ViewportInfo;
}

export function CanvasArea({
  canvasElRef,
  wrapperRef,
  onFileDropped,
  onFileSelected,
  hasImage,
  gridVisible,
  viewport,
}: CanvasAreaProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const file = e.dataTransfer.files[0];
      if (file && file.type.startsWith('image/')) {
        onFileDropped?.(file);
      }
    },
    [onFileDropped],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        onFileSelected?.(file);
      }
      e.target.value = '';
    },
    [onFileSelected],
  );

  const handleOpenFileClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const TRACK_SIZE = 8;
  const MIN_THUMB = 24;

  return (
    <div
      ref={wrapperRef}
      className="relative flex-1 overflow-hidden bg-neutral-200 dark:bg-neutral-800"
      style={
        gridVisible
          ? {
              backgroundImage:
                'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
              backgroundSize: '20px 20px',
              backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
            }
          : undefined
      }
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      {/* Fabric.js canvas element — Fabric wraps this in its own container */}
      <canvas ref={canvasElRef} />

      {/* Empty state: drop zone */}
      {!hasImage && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <div className="text-center p-8 rounded-lg bg-background/80 border-2 border-dashed border-muted-foreground/30 pointer-events-auto">
            <p className="text-lg font-medium text-foreground mb-2">Drop an image here</p>
            <p className="text-sm text-muted-foreground mb-4">or</p>
            <button
              type="button"
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors"
              onClick={handleOpenFileClick}
            >
              Open File
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileInputChange}
            />
          </div>
        </div>
      )}

      {/* Horizontal scrollbar */}
      {viewport.showH && (
        <div
          className="absolute left-0 bottom-0 pointer-events-none z-10"
          style={{
            right: viewport.showV ? TRACK_SIZE : 0,
            height: TRACK_SIZE,
          }}
        >
          <div className="relative w-full h-full bg-black/10 dark:bg-white/10 rounded-full">
            <div
              className="absolute top-0 h-full bg-black/30 dark:bg-white/30 rounded-full"
              style={{
                width: `${Math.max(MIN_THUMB / 100, viewport.thumbWidthRatio * 100)}%`,
                left: `${viewport.scrollXRatio * (1 - viewport.thumbWidthRatio) * 100}%`,
                minWidth: MIN_THUMB,
              }}
            />
          </div>
        </div>
      )}

      {/* Vertical scrollbar */}
      {viewport.showV && (
        <div
          className="absolute top-0 right-0 pointer-events-none z-10"
          style={{
            bottom: viewport.showH ? TRACK_SIZE : 0,
            width: TRACK_SIZE,
          }}
        >
          <div className="relative w-full h-full bg-black/10 dark:bg-white/10 rounded-full">
            <div
              className="absolute left-0 w-full bg-black/30 dark:bg-white/30 rounded-full"
              style={{
                height: `${Math.max(MIN_THUMB / 100, viewport.thumbHeightRatio * 100)}%`,
                top: `${viewport.scrollYRatio * (1 - viewport.thumbHeightRatio) * 100}%`,
                minHeight: MIN_THUMB,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
